import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import './Users.scss';
import dayjs from 'dayjs';
import { Role, User, UserList } from '../../types/userTypes';
import { EditAdminUserModal } from './EditAdminUserModal';
import { EditCourierUserModal } from './EditCourierUserModal';
import { fetchUsers } from 'fetch/fetchUsers';
import { Country, Region } from '../../types/orderTypes';
import { EditSenderUserModal } from './EditSenderUserModal';
import { CourierVehicle } from '../couriers/courierVehicle';
import { UsersMenu } from './UsersMenu';
import { RenderPaginationHeader } from './RenderPaginationHeader';
import { UsersTable } from './UsersTable';
import { getAuthHeadersFromCookie } from '../../lib/AuthClient/cookie';

export function formatTime(d: dayjs.Dayjs): string {
  return d && d.isValid() ? d.format('YYYY-MM-DD HH:mm') : '';
}

export function travelMode(user: User): JSX.Element | null {
  if (user.role === 'COURIER') {
    return <CourierVehicle courier={user} />;
  }
  return null;
}

export default function Users({ limitedAccess }: { limitedAccess?: boolean }) {
  const [loading, setLoading] = useState<boolean>(true);
  const [users, setUsers] = useState<UserList>(new UserList());
  const [searchText, setSearchText] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<Role[]>([]);
  const [regionsFilter, setRegionsFilter] = useState<Region>(
    limitedAccess && getAuthHeadersFromCookie().availableRegions[0]
  );
  const [countryFilter, setCountryFilter] = useState<Country>(null);
  const [showEditUserModal, setShowEditUserModal] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User>(null);
  const [userPhoneCopied, setUserPhoneCopied] = useState<any>(null);
  const [showLockedUsers, setShowLockedUsers] = useState<boolean>(false);

  const loadUsers = async (page: number): Promise<void> => {
    setLoading(true);
    try {
      const users = await fetchUsers(
        page,
        searchText,
        roleFilter,
        regionsFilter,
        countryFilter,
        showLockedUsers
      );
      setUsers(users);
    } catch (e) {
      console.log('Error fetching users', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await loadUsers(1);
    })();
  }, [roleFilter, regionsFilter, countryFilter]);

  const triggerSearchLoad = (): void => {
    loadUsers(1);
  };

  const toggleShowLockedUsers = (show: boolean): void => {
    setShowLockedUsers(show);
    triggerSearchLoad();
  };

  const roleFilterChange = (roles: Role[]): void => {
    setRoleFilter(roles);
  };

  const regionFilterChange = (region?: Region): void => {
    setRegionsFilter(region);
  };

  const countryFilterChange = (country?: Country): void => {
    setCountryFilter(country);
  };

  const userPhoneCopyMethod = (user: User): void => {
    setUserPhoneCopied(user.email);
    setTimeout(() => setUserPhoneCopied(null), 2500);
  };

  const changePage = (offset: number): void => {
    if (loading) return;
    const { page, totalPages } = users;
    const nextPage = page + offset;

    if (nextPage < 1 || nextPage > totalPages) return;

    setUsers(prev => {
      return {
        ...prev,
        page: nextPage,
        items: [],
      };
    });
    loadUsers(nextPage);
  };

  const userSelected = (user: User): void => {
    setSelectedUser(user);
    setShowEditUserModal(true);
  };

  const editModalClosed = (userUpdated: boolean): void => {
    setShowEditUserModal(false);
    userUpdated && loadUsers(users.page);
  };

  const renderSwitchRole = (role: string): JSX.Element => {
    switch (role) {
      case 'COURIER':
        return (
          <EditCourierUserModal
            user={selectedUser}
            onCloseModal={(userUpdated: boolean) =>
              editModalClosed(userUpdated)
            }
          />
        );
      case 'SENDER':
      case 'SENDER_ADMIN':
        return (
          <EditSenderUserModal
            user={selectedUser}
            onCloseModal={(userUpdated: boolean) =>
              editModalClosed(userUpdated)
            }
          />
        );
      case 'ADMIN':
        return (
          <EditAdminUserModal
            user={selectedUser}
            onCloseModal={(userUpdated: boolean) =>
              editModalClosed(userUpdated)
            }
          />
        );
    }
  };

  return (
    <>
      <Helmet>
        <title>Users</title>
      </Helmet>
      <UsersMenu limitedAccess={limitedAccess} />
      <section className="user-list">
        <h3>Users</h3>

        <div className="user--list__container">
          <RenderPaginationHeader
            loading={loading}
            searchText={searchText}
            setSearchText={setSearchText}
            page={users.page}
            pageSize={users.pageSize}
            items={users.items}
            total={users.total}
            totalPages={users.totalPages}
            roleFilter={roleFilter}
            regionFilter={regionsFilter}
            countryFilter={countryFilter}
            changePage={changePage}
            roleFilterChange={roleFilterChange}
            toggleShowLockedUsers={toggleShowLockedUsers}
            regionFilterChange={regionFilterChange}
            countryFilterChange={countryFilterChange}
            showLockedUsers={showLockedUsers}
            triggerSearchLoad={triggerSearchLoad}
            limitedAccess={limitedAccess}
          />

          <UsersTable
            userPhoneCopyMethod={userPhoneCopyMethod}
            userSelected={userSelected}
            userPhoneCopied={userPhoneCopied}
            loading={loading}
            users={users.items}
          />
        </div>
        <div>{showEditUserModal && renderSwitchRole(selectedUser?.role)}</div>
      </section>
    </>
  );
}
